import {Evaluator, registerNode} from "@cm/graph-v2-final"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {ImageOpContextImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-imgproc"
import {createImageOpImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc-utils"
import {assertNoBatching} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/mean-and-variance/definition"
import {ImageOpMeanAndVarianceCombinedHal} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/mean-and-variance/hal"
import {ImageOpExtractRegionHal} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-region/hal"
import {
    ImageOpOutputsOfType,
    ImageOpParametersOfType,
    SpecializingImageOpNode,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/specializing-image-op-node"
import {ImageOpInputsOrNodes} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {Descriptor} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {REF_COUNT_DEBUG_ID_INITIAL} from "@cm/utils"

@registerNode
export class ImageOpNodeMeanAndVariance extends SpecializingImageOpNode<typeof definition> {
    static nodeClass = "ImageOpNodeMeanAndVariance"
    static definition = definition

    constructor(parameters: ImageOpInputsOrNodes<typeof definition>) {
        super(parameters)
    }

    protected override dispose() {
        this._varianceView?.release(REF_COUNT_DEBUG_ID_INITIAL)
        this._meanView?.release(REF_COUNT_DEBUG_ID_INITIAL)
        this._combined?.release(REF_COUNT_DEBUG_ID_INITIAL)
        this._varianceView = null
        this._meanView = null
        this._combined = null
        super.dispose()
    }

    // Streams the source once (via ImageOpMeanAndVarianceCombinedHal, batched to bound atlas usage) into a
    // single 2x1 [mean, variance] image, then exposes each half as its own ImageOpExtractRegionHal view -
    // reusing that existing primitive rather than a hand-rolled extraction. The combined instance and its
    // two views are held as flat sibling fields (not nested inside one another) and persisted across
    // evaluations like ComplexSpecializingImageOpNode does for a single hal instance, so cached GPU output
    // survives re-evaluation. Views hold a ref to the combined instance (as its sourceImage); the combined
    // instance never references its views, so there's no reference cycle to unwind on disposal.
    protected toHal = async (
        evaluator: Evaluator<ImageOpContextHal>,
        parameters: ImageOpParametersOfType<typeof definition, "hal">,
    ): Promise<ImageOpOutputsOfType<typeof definition, "hal">> => {
        const evaluatedParameters = await evaluator.getAll(parameters)
        const combinedParameters = {
            ...evaluatedParameters,
            imageOpHalOptions: {
                usePhysicalOutputImage: true,
            },
        }
        if (!this._combined) {
            this._combined = new ImageOpMeanAndVarianceCombinedHal(evaluator.context, combinedParameters)
            this._combined.debugId = this.debugId ? `${this.debugId}.combined` : undefined
        } else {
            this._combined.setParameters(combinedParameters)
        }
        const viewParameters = (x: number) => ({
            imageOpHalOptions: {
                usePhysicalOutputImage: true,
            },
            sourceImage: this._combined!,
            region: {
                x,
                y: 0,
                width: 1,
                height: 1,
            },
            mipLevelOffset: 0,
            addressMode: "border" as const,
        })
        if (!this._meanView) {
            this._meanView = new ImageOpExtractRegionHal(evaluator.context, viewParameters(0))
            this._meanView.debugId = this.debugId ? `${this.debugId}.mean` : undefined
        } else {
            this._meanView.setParameters(viewParameters(0))
        }
        if (!this._varianceView) {
            this._varianceView = new ImageOpExtractRegionHal(evaluator.context, viewParameters(1))
            this._varianceView.debugId = this.debugId ? `${this.debugId}.variance` : undefined
        } else {
            this._varianceView.setParameters(viewParameters(1))
        }
        return {
            mean: this._meanView,
            variance: this._varianceView,
        }
    }

    protected override toImgProc = async (
        evaluator: Evaluator<ImageOpContextImgProc>,
        parameters: ImageOpParametersOfType<typeof definition, "imgproc">,
    ): Promise<ImageOpOutputsOfType<typeof definition, "imgproc">> => {
        const {sourceImage} = await evaluator.getAll(parameters)
        assertNoBatching(sourceImage.descriptor)
        const {channels, width, height} = sourceImage.descriptor
        const pixelCount = width * height
        const oneByOne: Descriptor = {width: 1, height: 1, channels}

        const sum = createImageOpImgProc(oneByOne, {type: "reduce", operation: "sum", input: sourceImage.node})
        const mean = createImageOpImgProc(oneByOne, {type: "math", operation: "*", firstInput: sum.node, secondInput: 1 / pixelCount})

        const squared = createImageOpImgProc(sourceImage.descriptor, {type: "math", operation: "square", firstInput: sourceImage.node, secondInput: undefined})
        const sumSquares = createImageOpImgProc(oneByOne, {type: "reduce", operation: "sum", input: squared.node})
        const meanOfSquares = createImageOpImgProc(oneByOne, {type: "math", operation: "*", firstInput: sumSquares.node, secondInput: 1 / pixelCount})

        const meanSquared = createImageOpImgProc(oneByOne, {type: "math", operation: "square", firstInput: mean.node, secondInput: undefined})
        const rawVariance = createImageOpImgProc(oneByOne, {type: "math", operation: "-", firstInput: meanOfSquares.node, secondInput: meanSquared.node})
        const variance = createImageOpImgProc(oneByOne, {type: "math", operation: "abs", firstInput: rawVariance.node, secondInput: undefined})

        return {mean, variance}
    }

    private _combined: ImageOpMeanAndVarianceCombinedHal | null = null
    private _meanView: ImageOpExtractRegionHal | null = null
    private _varianceView: ImageOpExtractRegionHal | null = null
}
